import { Link } from '@tanstack/react-router';
import { Sidebar, SidebarSection, sidebarItemVariants } from '@wundo/ui';
import { activePath } from '@/lib/navMatch.ts';
import { groupItems, type NavGroup } from './navConfig.ts';
import { NavItemLabel } from './NavItemLabel.tsx';

export interface GroupNavProps {
  group: NavGroup;
  pathname: string;
}

/*
 * The pages of the group you are standing in, as a second sidebar beside the
 * rail.
 *
 * Only from the desk breakpoint up, where 224px costs the page nothing it
 * needs. Narrower than that the same list is the flyout beside the rail tile,
 * and the shell renders one or the other — never both, or a screen reader would
 * be offered the group's pages twice.
 *
 * A list rather than tabs: a group can hold seventeen tools in five named runs,
 * and a tab strip that wraps onto four rows is not a strip. No header: the lit
 * rail tile beside it already says which group this is.
 */
export function GroupNav({ group, pathname }: GroupNavProps) {
  /*
   * One winner, the longest match: /dashboard/metrics-history sits under
   * /dashboard, and marking both rows current would say the page is in two
   * places at once.
   */
  const current = activePath(
    pathname,
    groupItems(group).map((item) => item.to),
  );

  return (
    <Sidebar aria-label={group.label}>
      {group.sections.map((section, index) => (
        <SidebarSection key={section.title ?? index} title={section.title}>
          {section.items.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              /*
               * The router's own active match is fuzzy and wins over the prop
               * below, so /dashboard would light up on /dashboard/metrics-history
               * too. Pinned to exact, the highlight is the longest match above
               * and nothing else.
               */
              activeOptions={{ exact: true }}
              aria-current={item.to === current ? 'page' : undefined}
              className={sidebarItemVariants({ active: item.to === current })}
            >
              <NavItemLabel item={item} />
            </Link>
          ))}
        </SidebarSection>
      ))}
    </Sidebar>
  );
}
